import JSZip from 'jszip';

export interface ZipFileEntry {
    name: string;
    path: string;
    size: number;
    isDirectory: boolean;
    children?: ZipFileEntry[];
}

export interface ZipParseResult {
    entries: ZipFileEntry[];
    totalFiles: number;
    totalSize: number;
    zipInstance?: JSZip;
    error?: string;
}

/**
 * Parse a zip file and return its file structure
 * @param file - The zip file to parse
 * @returns Promise with the parsed file structure
 */
export const parseZipFile = async (file: File): Promise<ZipParseResult> => {
    try {
        const zip = await JSZip.loadAsync(file);
        const entries: ZipFileEntry[] = [];

        // Build a tree structure from flat file list
        const fileMap = new Map<string, ZipFileEntry>();

        // First pass: collect all entries with their metadata
        const entryData: Array<{
            relativePath: string;
            zipEntry: JSZip.JSZipObject;
            parts: string[];
            isDirectory: boolean;
        }> = [];

        zip.forEach((relativePath, zipEntry) => {
            const parts = relativePath.split('/').filter(Boolean);
            const isDirectory = zipEntry.dir;
            entryData.push({ relativePath, zipEntry, parts, isDirectory });
        });

        // Process all entries in parallel
        const processedEntries = await Promise.all(
            entryData.map(async ({ relativePath, zipEntry, parts, isDirectory }) => {
                // Get actual uncompressed size using public API
                let size = 0;
                if (!isDirectory) {
                    try {
                        const data = await zipEntry.async('uint8array');
                        size = data.length;
                    } catch {
                        // If we can't read the file, treat it as 0 size
                        size = 0;
                    }
                }

                return {
                    relativePath,
                    parts,
                    size,
                    isDirectory,
                };
            })
        );

        // Build file tree from processed entries
        let totalFiles = 0;
        let totalSize = 0;

        processedEntries.forEach(({ relativePath, parts, size, isDirectory }) => {
            if (!isDirectory) {
                totalFiles++;
                totalSize += size;
            }

            // Create entry for this file/folder
            const entry: ZipFileEntry = {
                name: parts[parts.length - 1] || relativePath,
                path: relativePath,
                size,
                isDirectory,
                children: isDirectory ? [] : undefined,
            };

            fileMap.set(relativePath, entry);

            // Build parent path and create parent directories if needed
            let currentPath = '';
            for (let i = 0; i < parts.length - 1; i++) {
                const parentPath = currentPath ? `${currentPath}/${parts[i]}` : parts[i];
                if (!fileMap.has(parentPath + '/')) {
                    const parentEntry: ZipFileEntry = {
                        name: parts[i],
                        path: parentPath + '/',
                        size: 0,
                        isDirectory: true,
                        children: [],
                    };
                    fileMap.set(parentPath + '/', parentEntry);
                }
                currentPath = parentPath;
            }
        });

        // Build tree structure
        fileMap.forEach((entry, path) => {
            const parts = path.split('/').filter(Boolean);
            if (parts.length === 1) {
                // Root level entry
                entries.push(entry);
            } else {
                // Find parent and add as child
                const parentParts = parts.slice(0, -1);
                const parentPath = parentParts.join('/') + '/';
                const parent = fileMap.get(parentPath);
                if (parent?.children) {
                    parent.children.push(entry);
                }
            }
        });

        // Sort entries: directories first, then by name
        const sortEntries = (entries: ZipFileEntry[]) => {
            entries.sort((a, b) => {
                if (a.isDirectory !== b.isDirectory) {
                    return a.isDirectory ? -1 : 1;
                }
                return a.name.localeCompare(b.name);
            });
            entries.forEach(entry => {
                if (entry.children) {
                    sortEntries(entry.children);
                }
            });
        };
        sortEntries(entries);

        return {
            entries,
            totalFiles,
            totalSize,
            zipInstance: zip,
        };
    } catch (error) {
        return {
            entries: [],
            totalFiles: 0,
            totalSize: 0,
            error: error instanceof Error ? error.message : 'Failed to parse zip file',
        };
    }
};

/**
 * Read file content from zip
 * @param zipInstance - JSZip instance from parseZipFile
 * @param filePath - Path to the file in the zip
 * @returns Promise with the file content as string
 */
export const readFileFromZip = async (zipInstance: JSZip, filePath: string): Promise<string> => {
    const file = zipInstance.file(filePath);

    if (!file) {
        throw new Error('File not found in zip');
    }

    try {
        // Try to read as text first
        const content = await file.async('string');
        return content;
    } catch (error) {
        throw new Error('Failed to read file content');
    }
};

/**
 * Format bytes to human-readable size
 */
export const formatBytes = (bytes: number): string => {
    if (bytes === 0) {
        return '0 B';
    }
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
};
